// @ts-nocheck
import { interactivityRegistration } from "./Charta";
import { DomQuery, HtmlClassSwitch, WithClass } from "./Dom";
import { DataChangedEvent } from "./Event";
import { application } from "./WebApp.Instance";
import { LoadingIndicator } from "./WebApp.LoadingIndicator";
import { ToastMessage } from "./WebApp.ToastBox";
import { Labels, RefreshMode, WebPageComponent } from "./WebSite.Page.Component";

export class Diagram extends WebPageComponent {
  toolbar: HTMLElement;
  contents: HTMLElement;
  labels: Labels;
  updating: HtmlClassSwitch;
  loadingIndicator: LoadingIndicator;
  diagramRenderer: DiagramToImageRenderer;
  interaction: DiagramInteraction | null;
  copyButton: HTMLButtonElement;
  downloadButton: HTMLButtonElement;
  shareButton: HTMLButtonElement;
  diagram: SVGSVGElement;
  legend: HTMLElement;

  constructor(element) {
    super(element);

    const query = new DomQuery(this.element);

    this.toolbar = query.getChild(WithClass("Toolbar"));
    this.contents = query.getChild(WithClass("Contents"));
    this.labels = new Labels(query.getChild(WithClass("Labels")));

    this.updating = new HtmlClassSwitch(this.element, "Updating");
    this.loadingIndicator = new LoadingIndicator();
    this.element.appendChild(this.loadingIndicator.element);

    this.diagramRenderer = new DiagramToImageRenderer(this);
    this.interaction = null;

    this.attachButtonHandlers();
  }

  get renderTime(): string {
    return this.element.dataset.RenderTime;
  }

  get interactionMode(): string {
    return this.element.dataset.Interaction;
  }

  get crosshairs(): Set<string> {
    return new Set((this.element.dataset.Crosshairs || "").split(" ").filter(Boolean));
  }

  get indexDimension(): string {
    return this.element.dataset.Index || "Horizontal";
  }

  get hoverSnap(): number {
    return Number(this.element.dataset.HoverSnap);
  }

  get uri(): string {
    return this.element.dataset.Uri;
  }

  determineComponents() {
    if (this.contents.childNodes.length === 2) {
      this.diagram = this.contents.childNodes[0].childNodes[0];
      this.legend = this.contents.childNodes[1];

      this.copyButton.disabled = !this.diagramRenderer.canCopy();
      this.downloadButton.disabled = false;
      this.shareButton.disabled = !this.diagramRenderer.canShare();

      this.interaction?.destroy();

      this.interaction = this.interactionMode === "Disabled"
        ? null
        : new DiagramInteraction(this.diagram, this.legend, this.labels, this.interactionMode, this.crosshairs, this.indexDimension, this.hoverSnap);
    }
  }

  attachButtonHandlers() {
    this.copyButton = new DomQuery(this.toolbar).getChild(WithClass("Copy"));
    this.copyButton.addEventListener("click", (event) => this.copy());
    this.copyButton.disabled = true;

    this.downloadButton = new DomQuery(this.toolbar).getChild(WithClass("Download"));
    this.downloadButton.addEventListener("click", (event) => this.download());
    this.downloadButton.disabled = true;

    this.shareButton = new DomQuery(this.toolbar).getChild(WithClass("Share"));
    this.shareButton.addEventListener("click", (event) => this.share());
    this.shareButton.disabled = true;
  }

  bind() {
    if (this.renderTime === "Deferred")
      this.refresh();
    else
      this.determineComponents();
  }

  copy() {
    this.diagramRenderer.copy();
  }

  download() {
    this.diagramRenderer.download("Diagram");
  }

  share() {
    this.diagramRenderer.share("Diagram");
  }

  reload(text) {
    const dummyElement = document.createElement("div");
    dummyElement.innerHTML = text;

    const contents = new DomQuery(dummyElement).getDescendant(WithClass("Contents"));

    this.element.replaceChild(contents, this.contents);
    this.contents = contents;

    this.determineComponents();
  }

  refresh() {
    this.updating.setStatus(true);
    this.loadingIndicator.setStatus(true);

    fetch(
      this.uri,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ "Command": "Refresh" })
      })
      .then((response) => response.text())
      .then(text => this.reload(text))
      .finally(() => {
        this.updating.setStatus(false);
        this.loadingIndicator.setStatus(false);
      });
  }

  handleEvent(event) {
    if (event instanceof DataChangedEvent && this.refreshMode === RefreshMode.ForSiblings)
      this.invalidate();
    else
      super.handleEvent(event);
  }

  async validate() {
    this.refresh();
  }
}

export class DiagramToImageRenderer {
  constructor(diagram) {
    this.component = diagram;
  }

  copy() {
    this.createImage(
      (canvas) => {
        canvas.toBlob(
          blob => {
            navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
              .then(() => { application.toastBox.addMessage(new ToastMessage(this.component.labels.DiagramCopiedSuccessfully, "Success")); })
              .catch((error) => { application.toastBox.addMessage(new ToastMessage(this.component.labels.FailedToCopyDiagram, "Error")); });
          },
          "image/png"
        );
      }
    );
  }

  canCopy() {
    return 'ClipboardItem' in window;
  }

  canShare() {
    return typeof navigator.canShare === "function";
  }

  download(name) {
    this.createImage(
      (canvas) => {
        const pngData = canvas.toDataURL("image/png");

        const downloadLink = document.createElement("a");
        downloadLink.href = pngData;
        downloadLink.download = name + ".png";
        downloadLink.click();
      }
    );
  }

  share(name) {
    this.createImage(
      (canvas) => {
        canvas.toBlob(
          (blob) => {
            const file = new File([blob], name + ".png", { type: "image/png" });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              navigator.share({
                title: name,
                files: [file]
              });
            }
          },
          "image/png"
        );
      }
    );
  }

  applyComputedStyles(original, clone) {
    const computedStyle = window.getComputedStyle(original);

    for (let index = 0; index < computedStyle.length; index++) {
      const name = computedStyle[index];
      const value = computedStyle.getPropertyValue(name);

      clone.style[name] = value;
    }

    for (let index = 0; index < original.children.length; index++)
      this.applyComputedStyles(original.children[index], clone.children[index]);
  }

  createImage(onload) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    const diagram = this.component.diagram.cloneNode(true);
    this.applyComputedStyles(this.component.diagram, diagram);

    const diagramWidth = this.component.diagram.getBoundingClientRect().width;
    const diagramHeight = this.component.diagram.getBoundingClientRect().height;
    const legendWidth = 350;

    const legend = this.component.legend.cloneNode(true);
    this.applyComputedStyles(this.component.legend, legend);
    legend.style.width = legendWidth + "px";
    legend.style.height = diagramHeight;

    canvas.width = diagramWidth + legendWidth;
    canvas.height = diagramHeight;

    const legendContainer = document.createElement("div");
    legendContainer.appendChild(legend);

    const container = document.createElement("div");
    container.style.background = "white";
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.width = canvas.width + "px";
    container.appendChild(diagram);
    container.appendChild(legendContainer);

    const foreignObject = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
    foreignObject.setAttribute("width", "100%");
    foreignObject.setAttribute("height", "100%");
    foreignObject.appendChild(container);

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.appendChild(foreignObject);

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const image = new Image();
    image.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgString)));

    image.onload = function () {
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      onload(canvas);
    };
  }
}

const svgNamespace = "http://www.w3.org/2000/svg";

const tooltipOffset = 14;

interface DiagramVertex {
  x: number;
  y: number;
  xLabel: string;
  yLabel: string;
  segment: string;
  range: string;
  runIndex: string;
}

interface DiagramSeries {
  group: SVGGElement;
  scatter: boolean;
  vertices: DiagramVertex[];
  color: string;
  strokeWidth: number;
  dashArray: number[];
  markerRadius: number;
  name: string;
}

interface DiagramShape {
  element: SVGElement;
  label: string;
  color: string;
  header: string;
  category: string;
  range: string;
  structured: boolean;
  lane: string;
}

interface DiagramBand {
  group: SVGGElement;
  name: string;
  color: string;
}

interface OverlayPoint {
  x: number;
  y: number;
}

interface NearestHit {
  vertex: DiagramVertex;
  series: DiagramSeries;
  seriesIndex: number;
  distance: number;
}

interface ListenerBinding {
  target: EventTarget;
  type: string;
  handler: EventListener;
}

class DiagramModel {
  svg: SVGSVGElement;
  series: DiagramSeries[];
  elements: DiagramShape[];
  bands: DiagramBand[];

  constructor(svg: SVGSVGElement) {
    this.svg = svg;
    this.series = this.collectSeries();
    this.elements = this.collectElements();
    this.bands = this.collectBands();
  }

  collectBands(): DiagramBand[] {
    const result: DiagramBand[] = [];

    this.svg.querySelectorAll("g.DataSeries").forEach(
      (group) => {
        if (group.querySelector(".Band") !== null) {
          result.push({
            group: group,
            name: group.getAttribute("data-series-name") || "",
            color: group.getAttribute("data-series-color") || "currentColor"
          });
        }
      }
    );

    return result;
  }

  collectSeries(): DiagramSeries[] {
    const groups = this.svg.querySelectorAll("g.DataSeries");
    const result: DiagramSeries[] = [];

    groups.forEach(
      (group) => {
        const anchors = group.querySelectorAll(".DataPoint");

        if (anchors.length > 0 && group.getAttribute("data-series-tooltip") !== null) {
          const vertices = Array.from(anchors).map(
            (anchor) => ({
              x: Number(anchor.getAttribute("cx")),
              y: Number(anchor.getAttribute("cy")),
              xLabel: anchor.getAttribute("data-x") || "",
              yLabel: anchor.getAttribute("data-y") || "",
              segment: anchor.getAttribute("data-segment") || "",
              range: anchor.getAttribute("data-range") || "",
              runIndex: anchor.getAttribute("data-run-index") || ""
            })
          );

          const line = group.querySelector("polyline, path");
          const marker = group.querySelector("circle:not(.DataPoint)");
          const strokeWidth = line?.getAttribute("stroke-width");
          const dashArray = line?.getAttribute("stroke-dasharray");

          result.push({
            group: group,
            scatter: group.querySelector(".Scatter") !== null,
            vertices: vertices,
            color: group.getAttribute("data-series-color") || "currentColor",
            strokeWidth: strokeWidth ? Number(strokeWidth) : 0,
            dashArray: dashArray ? dashArray.split(",").map(Number) : [],
            markerRadius: marker ? Number(marker.getAttribute("r")) : 0,
            name: group.getAttribute("data-series-name") || ""
          });
        }
      }
    );

    return result;
  }

  collectElements(): DiagramShape[] {
    const result: DiagramShape[] = [];

    this.svg.querySelectorAll(".PieSlice, .Column, .Bar").forEach(
      (element) => {
        const value = element.getAttribute("data-value");
        const category = element.getAttribute("data-y");

        if (value !== null || category !== null) {
          const group = element.closest("g.DataSeries");

          result.push({
            element: element,
            label: value?.trim() ?? "",
            color: element.getAttribute("fill") ?? "currentColor",
            header: group?.getAttribute("data-series-name") ?? "",
            category: category ?? "",
            range: element.getAttribute("data-x") ?? "",
            structured: category !== null,
            lane: element.getAttribute("data-lane") ?? ""
          });
        }
      }
    );

    return result;
  }
}

class DiagramTooltip {
  overlay: SVGSVGElement;
  element: HTMLElement;

  constructor(container: Element, overlay: SVGSVGElement) {
    this.overlay = overlay;

    this.element = document.createElement("div");
    this.element.setAttribute("class", "DiagramTooltip");

    container.appendChild(this.element);
  }

  clear(): void {
    this.element.replaceChildren();
  }

  addHeader(text: string): void {
    const header = document.createElement("div");
    header.className = "Header";
    header.textContent = text;

    this.element.appendChild(header);
  }

  addRow(color: string, name: string, value: string): void {
    const swatch = document.createElement("span");
    swatch.className = "Swatch";
    swatch.style.background = color;

    const label = document.createElement("span");
    label.className = "Name";
    label.textContent = name;

    const amount = document.createElement("span");
    amount.className = "Value";
    amount.textContent = value;

    const row = document.createElement("div");
    row.className = "Row";
    row.appendChild(swatch);
    row.appendChild(label);
    row.appendChild(amount);

    this.element.appendChild(row);
  }

  addLabelRow(color: string, label: string): void {
    const swatch = document.createElement("span");
    swatch.className = "Swatch";
    swatch.style.background = color;

    const text = document.createElement("span");
    text.className = "Name";
    text.textContent = label;

    const row = document.createElement("div");
    row.className = "Row";
    row.appendChild(swatch);
    row.appendChild(text);

    this.element.appendChild(row);
  }

  addSegment(segment: string): void {
    const row = document.createElement("div");
    row.className = "Segment";
    row.textContent = segment;

    this.element.appendChild(row);
  }

  positionAtAnchor(anchorX: number): void {
    this.element.classList.add("Visible");

    const overlayWidth = this.overlay.getBoundingClientRect().width;
    let left = anchorX + tooltipOffset;

    if (left + this.element.offsetWidth > overlayWidth)
      left = anchorX - this.element.offsetWidth - tooltipOffset;

    this.element.style.left = Math.max(0, left) + "px";
    this.element.style.top = "8px";
  }

  positionAtRow(anchorY: number): void {
    this.element.classList.add("Visible");

    const overlayHeight = this.overlay.getBoundingClientRect().height;
    let top = anchorY + tooltipOffset;

    if (top + this.element.offsetHeight > overlayHeight)
      top = anchorY - this.element.offsetHeight - tooltipOffset;

    this.element.style.top = Math.max(0, top) + "px";
    this.element.style.left = "8px";
  }

  positionNearPoint(anchorX: number, anchorY: number): void {
    this.element.classList.add("Visible");

    const box = this.overlay.getBoundingClientRect();
    let left = anchorX + tooltipOffset;

    if (left + this.element.offsetWidth > box.width)
      left = anchorX - this.element.offsetWidth - tooltipOffset;

    let top = anchorY + tooltipOffset;

    if (top + this.element.offsetHeight > box.height)
      top = anchorY - this.element.offsetHeight - tooltipOffset;

    this.element.style.left = Math.max(0, left) + "px";
    this.element.style.top = Math.max(0, top) + "px";
  }

  positionAtCursor(event: MouseEvent): void {
    this.element.classList.add("Visible");

    const box = this.overlay.getBoundingClientRect();
    let left = event.clientX - box.left + tooltipOffset;

    if (left + this.element.offsetWidth > box.width)
      left = event.clientX - box.left - this.element.offsetWidth - tooltipOffset;

    this.element.style.left = Math.max(0, left) + "px";
    this.element.style.top = (event.clientY - box.top + tooltipOffset) + "px";
  }

  hide(): void {
    this.element.classList.remove("Visible");
  }

  remove(): void {
    this.element.remove();
  }
}

class OverlayProjection {
  matrix: DOMMatrix;
  left: number;
  top: number;

  constructor(matrix: DOMMatrix, left: number, top: number) {
    this.matrix = matrix;
    this.left = left;
    this.top = top;
  }

  get scale(): number {
    return this.matrix.a;
  }

  toOverlay(userX: number, userY: number): OverlayPoint {
    const screenX = this.matrix.a * userX + this.matrix.c * userY + this.matrix.e;
    const screenY = this.matrix.b * userX + this.matrix.d * userY + this.matrix.f;

    return this.fromClient(screenX, screenY);
  }

  toUser(clientX: number, clientY: number): OverlayPoint {
    const inverse = this.matrix.inverse();
    const userX = inverse.a * clientX + inverse.c * clientY + inverse.e;
    const userY = inverse.b * clientX + inverse.d * clientY + inverse.f;

    return { x: userX, y: userY };
  }

  fromClient(clientX: number, clientY: number): OverlayPoint {
    return { x: clientX - this.left, y: clientY - this.top };
  }

  squaredDistanceTo(vertex: DiagramVertex, cursor: OverlayPoint): number {
    const point = this.toOverlay(vertex.x, vertex.y);
    const dx = point.x - cursor.x;
    const dy = point.y - cursor.y;

    return dx * dx + dy * dy;
  }
}

class DiagramOverlay {
  svg: SVGSVGElement;
  element: SVGSVGElement;
  tooltip: DiagramTooltip;

  crosshairs: SVGLineElement[] = [];
  markers: SVGCircleElement[];
  segmentHighlights: SVGElement[] = [];
  rangeHighlights: SVGElement[] = [];

  constructor(svg: SVGSVGElement, container: Element, series: DiagramSeries[]) {
    this.svg = svg;

    this.element = document.createElementNS(svgNamespace, "svg");
    this.element.setAttribute("class", "InteractionOverlay");

    this.markers = series.map(
      (entry) => {
        const marker = document.createElementNS(svgNamespace, "circle");
        marker.classList.add("HighlightMarker", "Hidden");
        marker.setAttribute("fill", entry.color);

        this.element.appendChild(marker);
        return marker;
      }
    );

    container.appendChild(this.element);

    this.tooltip = new DiagramTooltip(container, this.element);
  }

  addCrosshair(x1: number, y1: number, x2: number, y2: number): void {
    const line = document.createElementNS(svgNamespace, "line");
    line.setAttribute("class", "Crosshair");
    line.setAttribute("x1", x1);
    line.setAttribute("y1", y1);
    line.setAttribute("x2", x2);
    line.setAttribute("y2", y2);

    this.element.insertBefore(line, this.element.firstChild);
    this.crosshairs.push(line);
  }

  clearCrosshairs(): void {
    this.crosshairs.forEach((crosshair) => crosshair.remove());
    this.crosshairs = [];
  }

  projection(): OverlayProjection {
    const matrix = this.svg.getScreenCTM() ?? new DOMMatrix();
    const box = this.element.getBoundingClientRect();

    return new OverlayProjection(matrix, box.left, box.top);
  }

  addSegmentHighlight(highlight: SVGElement): void {
    highlight.setAttribute("class", "SegmentHighlight");

    this.element.appendChild(highlight);
    this.segmentHighlights.push(highlight);
  }

  clearSegmentHighlights(): void {
    this.segmentHighlights.forEach((highlight) => highlight.remove());
    this.segmentHighlights = [];
  }

  addRangeHighlight(points: OverlayPoint[], color: string): void {
    points.forEach(
      (point) => {
        const marker = document.createElementNS(svgNamespace, "circle");
        marker.classList.add("HighlightMarker");
        marker.setAttribute("fill", color);
        marker.setAttribute("cx", point.x);
        marker.setAttribute("cy", point.y);

        this.element.appendChild(marker);
        this.rangeHighlights.push(marker);
      }
    );
  }

  clearRangeHighlights(): void {
    this.rangeHighlights.forEach((highlight) => highlight.remove());
    this.rangeHighlights = [];
  }

  hide(): void {
    this.clearCrosshairs();
    this.markers.forEach((marker) => marker.classList.add("Hidden"));
    this.clearRangeHighlights();
    this.tooltip.hide();
  }

  remove(): void {
    this.element.remove();
    this.tooltip.remove();
  }
}

class SeriesHover {
  svg: SVGSVGElement;
  model: DiagramModel;
  overlay: DiagramOverlay;
  mode: string;
  crosshairs: Set<string>;
  indexDimension: string;
  snap: number;
  listeners: ListenerBinding[];
  pendingEvent: MouseEvent | null;
  frameHandle: number;

  constructor(svg: SVGSVGElement, model: DiagramModel, overlay: DiagramOverlay, mode: string, crosshairs: Set<string>, indexDimension: string, snap: number) {
    this.svg = svg;
    this.model = model;
    this.overlay = overlay;
    this.mode = mode;
    this.crosshairs = crosshairs;
    this.indexDimension = indexDimension;
    this.snap = snap;
    this.listeners = [];
    this.pendingEvent = null;
    this.frameHandle = 0;

    this.listen(svg, "mousemove", (event) => this.scheduleMove(event));
    this.listen(svg, "mouseleave", (event) => this.leave());
  }

  listen(target: EventTarget, type: string, handler: EventListener): void {
    target.addEventListener(type, handler);
    this.listeners.push({ target: target, type: type, handler: handler });
  }

  scheduleMove(event: MouseEvent): void {
    this.pendingEvent = event;

    if (this.frameHandle === 0)
      this.frameHandle = requestAnimationFrame(() => this.flushMove());
  }

  flushMove(): void {
    this.frameHandle = 0;

    if (this.pendingEvent !== null)
      this.move(this.pendingEvent);

    this.pendingEvent = null;
  }

  cancelFrame(): void {
    if (this.frameHandle !== 0) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = 0;
    }

    this.pendingEvent = null;
  }

  leave(): void {
    this.cancelFrame();
    this.overlay.hide();
  }

  move(event: MouseEvent): void {
    if (this.mode === "NearestPoint")
      this.moveNearest(event);
    else
      this.moveUnified(event);
  }

  bandsAt(clientX: number, clientY: number): DiagramBand[] {
    const result: DiagramBand[] = [];

    this.model.bands.forEach(
      (band) => {
        const box = band.group.getBoundingClientRect();
        const inside = clientX >= box.left && clientX <= box.right && clientY >= box.top && clientY <= box.bottom;

        if (inside && !band.group.classList.contains("Hidden"))
          result.push(band);
      }
    );

    return result;
  }

  addBandRows(tooltip: DiagramTooltip, clientX: number, clientY: number): number {
    const bands = this.bandsAt(clientX, clientY);
    bands.forEach((band) => tooltip.addLabelRow(band.color, band.name));

    return bands.length;
  }

  nearest(distance: (vertex: DiagramVertex) => number): NearestHit | null {
    let best: NearestHit | null = null;
    let bestDistance = Infinity;

    this.model.series.forEach(
      (entry, seriesIndex) => {
        if (!entry.group.classList.contains("Hidden"))
          entry.vertices.forEach(
            (vertex) => {
              const value = distance(vertex);

              if (value < bestDistance) {
                bestDistance = value;
                best = { vertex: vertex, series: entry, seriesIndex: seriesIndex, distance: value };
              }
            }
          );
      }
    );

    return best;
  }

  findNearestWithinSnap(cursor: OverlayPoint, projection: OverlayProjection): NearestHit | null {
    const best = this.nearest((vertex) => projection.squaredDistanceTo(vertex, cursor));

    if (best === null)
      return null;

    if (this.snap > 0 && best.distance > this.snap * this.snap)
      return null;

    return best;
  }

  showBandsOnly(event: MouseEvent): void {
    this.overlay.hide();

    const tooltip = this.overlay.tooltip;
    tooltip.clear();

    if (this.addBandRows(tooltip, event.clientX, event.clientY) > 0)
      tooltip.positionAtCursor(event);
  }

  moveNearest(event: MouseEvent): void {
    this.overlay.clearCrosshairs();
    this.overlay.clearRangeHighlights();

    const projection = this.overlay.projection();
    const cursor = projection.fromClient(event.clientX, event.clientY);

    const nearest = this.findNearestWithinSnap(cursor, projection);

    if (nearest === null)
      this.showBandsOnly(event);
    else {
      const position = projection.toOverlay(nearest.vertex.x, nearest.vertex.y);

      const endpoints = nearest.vertex.range === ""
        ? [nearest.vertex]
        : nearest.series.vertices.filter((candidate) => candidate.range === nearest.vertex.range);

      const points = endpoints.map((vertex) => projection.toOverlay(vertex.x, vertex.y));
      this.drawCrosshairs(points, projection);

      if (endpoints.length > 1) {
        this.overlay.markers.forEach((marker) => marker.classList.add("Hidden"));
        this.overlay.addRangeHighlight(points, nearest.series.color);
      }
      else
        this.overlay.markers.forEach(
          (marker, index) => {
            if (index !== nearest.seriesIndex)
              marker.classList.add("Hidden");
            else {
              marker.setAttribute("cx", position.x);
              marker.setAttribute("cy", position.y);
              marker.classList.remove("Hidden");
            }
          }
        );

      const tooltip = this.overlay.tooltip;
      tooltip.clear();

      if (nearest.series.name !== "")
        tooltip.addHeader(nearest.series.name);

      tooltip.addLabelRow(nearest.series.color, nearest.vertex.xLabel + ", " + nearest.vertex.yLabel);

      if (nearest.vertex.segment !== "")
        tooltip.addSegment(nearest.vertex.segment);

      this.addBandRows(tooltip, event.clientX, event.clientY);
      tooltip.positionNearPoint(position.x, position.y);
    }
  }

  moveUnified(event: MouseEvent): void {
    this.overlay.clearCrosshairs();
    this.overlay.clearRangeHighlights();

    const axis = this.indexDimension === "Vertical" ? "y" : "x";
    const indexLabel = axis === "x" ? "xLabel" : "yLabel";
    const valueLabel = axis === "x" ? "yLabel" : "xLabel";

    const projection = this.overlay.projection();
    const userAxis = projection.toUser(event.clientX, event.clientY)[axis];
    const target = this.nearest((vertex) => Math.abs(vertex[axis] - userAxis));

    if (target === null)
      this.overlay.hide();
    else {
      const points: OverlayPoint[] = [];

      const tooltip = this.overlay.tooltip;
      tooltip.clear();
      tooltip.addHeader(target.vertex[indexLabel]);

      this.model.series.forEach(
        (entry, seriesIndex) => {
          const marker = this.overlay.markers[seriesIndex];
          const matches = entry.vertices.filter((candidate) => candidate[axis] === target.vertex[axis]);
          const ranges = this.pairRanges(matches);

          if (entry.group.classList.contains("Hidden") || matches.length === 0)
            marker.classList.add("Hidden");
          else if (ranges.length > 0) {
            marker.classList.add("Hidden");

            ranges.forEach(
              (endpoints) => {
                const endpointPoints = endpoints.map((vertex) => projection.toOverlay(vertex.x, vertex.y));

                this.overlay.addRangeHighlight(endpointPoints, entry.color);
                endpointPoints.forEach((point) => points.push(point));

                tooltip.addRow(entry.color, entry.name, endpoints[0][valueLabel]);

                if (endpoints[0].segment !== "")
                  tooltip.addSegment(endpoints[0].segment);
              }
            );
          }
          else {
            const vertex = matches[0];
            const position = projection.toOverlay(vertex.x, vertex.y);

            marker.setAttribute("cx", position.x);
            marker.setAttribute("cy", position.y);
            marker.classList.remove("Hidden");
            points.push(position);

            tooltip.addRow(entry.color, entry.name, vertex[valueLabel]);

            if (vertex.segment !== "")
              tooltip.addSegment(vertex.segment);
          }
        }
      );

      this.addBandRows(tooltip, event.clientX, event.clientY);
      this.drawCrosshairs(points, projection);

      if (axis === "x")
        tooltip.positionAtAnchor(projection.toOverlay(target.vertex.x, 0).x);
      else
        tooltip.positionAtRow(projection.toOverlay(0, target.vertex.y).y);
    }
  }

  pairRanges(vertices: DiagramVertex[]): DiagramVertex[][] {
    const groups = new Map<string, DiagramVertex[]>();

    vertices.forEach(
      (vertex) => {
        if (vertex.range !== "") {
          const members = groups.get(vertex.range) || [];
          members.push(vertex);
          groups.set(vertex.range, members);
        }
      }
    );

    const result: DiagramVertex[][] = [];

    groups.forEach(
      (members) => {
        if (members.length > 1)
          result.push(members);
      }
    );

    return result;
  }

  plotBounds(): DOMRect {
    const plotArea = this.svg.querySelector(".PlotArea");

    return (plotArea !== null ? plotArea : this.svg).getBoundingClientRect();
  }

  drawCrosshairs(points: OverlayPoint[], projection: OverlayProjection): void {
    const bounds = this.plotBounds();
    const topLeft = projection.fromClient(bounds.left, bounds.top);
    const bottomRight = projection.fromClient(bounds.right, bounds.bottom);

    if (this.crosshairs.has("Horizontal"))
      new Set(points.map((point) => point.x)).forEach((x) => this.overlay.addCrosshair(x, topLeft.y, x, bottomRight.y));

    if (this.crosshairs.has("Vertical"))
      new Set(points.map((point) => point.y)).forEach((y) => this.overlay.addCrosshair(topLeft.x, y, bottomRight.x, y));
  }

  destroy(): void {
    this.cancelFrame();
    this.listeners.forEach((binding) => binding.target.removeEventListener(binding.type, binding.handler));
  }
}

class SegmentHighlighter {
  overlay: DiagramOverlay;

  constructor(overlay: DiagramOverlay) {
    this.overlay = overlay;
  }

  highlight(segment: string, series: DiagramSeries): void {
    this.clear();

    const vertices = series.vertices.filter((vertex) => vertex.segment === segment);

    if (vertices.length > 0) {
      const projection = this.overlay.projection();

      if (series.scatter)
        vertices.forEach((vertex) => this.highlightPoint(series, vertex, projection));
      else
        this.segmentRuns(series, segment).forEach(
          (run) => {
            if (run.length > 1)
              this.highlightRun(series, run, projection);
            else
              this.highlightPoint(series, run[0], projection);
          }
        );
    }
  }

  highlightRun(series: DiagramSeries, run: DiagramVertex[], projection: OverlayProjection): void {
    const points = run.map(
      (vertex) => {
        const point = projection.toOverlay(vertex.x, vertex.y);
        return point.x + "," + point.y;
      }
    ).join(" ");

    const scale = projection.scale;

    const highlight = document.createElementNS(svgNamespace, "polyline");
    highlight.setAttribute("fill", "none");
    highlight.setAttribute("stroke", series.color);
    highlight.setAttribute("points", points);

    if (series.strokeWidth > 0)
      highlight.setAttribute("stroke-width", series.strokeWidth * scale);

    if (series.dashArray.length > 0) {
      highlight.setAttribute("stroke-dasharray", series.dashArray.map((width) => width * scale).join(","));
      highlight.setAttribute("stroke-dashoffset", this.precedingLength(series, run[0]) * scale);
    }

    this.overlay.addSegmentHighlight(highlight);
  }

  precedingLength(series: DiagramSeries, start: DiagramVertex): number {
    const runVertices = series.vertices.filter((vertex) => vertex.runIndex === start.runIndex);
    const startIndex = runVertices.indexOf(start);
    let length = 0;

    for (let index = 0; index < startIndex; index += 1)
      length += Math.hypot(runVertices[index + 1].x - runVertices[index].x, runVertices[index + 1].y - runVertices[index].y);

    return length;
  }

  highlightPoint(series: DiagramSeries, vertex: DiagramVertex, projection: OverlayProjection): void {
    const point = projection.toOverlay(vertex.x, vertex.y);
    const scale = projection.scale;
    const radius = (series.markerRadius > 0 ? series.markerRadius : Math.max(3, series.strokeWidth)) * scale;

    const highlight = document.createElementNS(svgNamespace, "circle");
    highlight.setAttribute("fill", series.color);
    highlight.setAttribute("cx", point.x);
    highlight.setAttribute("cy", point.y);
    highlight.setAttribute("r", radius);

    this.overlay.addSegmentHighlight(highlight);
  }

  segmentRuns(series: DiagramSeries, segment: string): DiagramVertex[][] {
    const runs: DiagramVertex[][] = [];
    const vertices = series.vertices;
    let index = 0;

    while (index < vertices.length) {
      if (vertices[index].segment === segment) {
        const run: DiagramVertex[] = [];
        const runIndex = vertices[index].runIndex;

        while (index < vertices.length && vertices[index].segment === segment && vertices[index].runIndex === runIndex) {
          run.push(vertices[index]);
          index += 1;
        }

        runs.push(run);
      }
      else
        index += 1;
    }

    return runs;
  }

  clear(): void {
    this.overlay.clearSegmentHighlights();
  }

  destroy(): void {
    this.clear();
  }
}

class LegendController {
  svg: SVGSVGElement;
  model: DiagramModel;
  labels: any;
  highlighter: SegmentHighlighter | null;
  legendItems: Element[];
  legendTargets: Element[];
  allTargets: Element[];
  listeners: ListenerBinding[];

  constructor(svg: SVGSVGElement, model: DiagramModel, legend: Element | null, labels: any, highlighter: SegmentHighlighter | null) {
    this.svg = svg;
    this.model = model;
    this.labels = labels;
    this.highlighter = highlighter;
    this.legendItems = [];
    this.legendTargets = [];
    this.allTargets = [];
    this.listeners = [];

    if (legend !== null) {
      this.attachSeries(legend);
      this.attachSegments();
    }
  }

  listen(target: EventTarget, type: string, handler: EventListener): void {
    target.addEventListener(type, handler);
    this.listeners.push({ target: target, type: type, handler: handler });
  }

  attachSeries(legend: Element): void {
    const items = Array.from(legend.querySelectorAll(".LegendItem"));
    const targets = Array.from(this.svg.querySelectorAll("g.DataSeries, g.Columns g.Series, .PieSlice"));

    const pairs = this.pairLegend(items, targets);

    this.allTargets = targets;
    this.legendItems = pairs.map((pair) => pair.item);
    this.legendTargets = pairs.map((pair) => pair.target);

    this.legendItems.forEach(
      (item, index) => {
        const target = this.legendTargets[index];
        const control = item.querySelector(".LegendHeader") || item;

        control.classList.add("Interactive");

        this.listen(control, "mouseenter", (event) => this.highlightSeries(index));
        this.listen(control, "mouseleave", (event) => this.clearHighlight());

        if (item.querySelector("table") === null && !target.classList.contains("PieSlice")) {
          control.classList.add("Toggleable");

          if (this.labels !== null && this.labels.ShowOrHideSeries !== undefined)
            control.title = this.labels.ShowOrHideSeries.textContent;

          this.listen(control, "click", (event) => this.toggleSeries(index));
        }
      }
    );
  }

  pairLegend(items: Element[], targets: Element[]): { item: Element; target: Element }[] {
    const targetsById = new Map<string, Element>();

    targets.forEach(
      (target) => {
        const id = target.getAttribute("data-series-id");

        if (id !== null && id !== "")
          targetsById.set(id, target);
      }
    );

    const pairs: { item: Element; target: Element }[] = [];
    const looseItems: Element[] = [];

    items.forEach(
      (item) => {
        const id = (item as HTMLElement).dataset.SeriesId;
        const target = (id !== undefined && id !== "") ? targetsById.get(id) : undefined;

        if (target !== undefined)
          pairs.push({ item: item, target: target });
        else
          looseItems.push(item);
      }
    );

    const looseTargets = targets.filter(
      (target) => {
        const id = target.getAttribute("data-series-id");
        return id === null || id === "";
      }
    );

    if (looseItems.length === looseTargets.length)
      looseItems.forEach((item, index) => pairs.push({ item: item, target: looseTargets[index] }));

    return pairs;
  }

  highlightSeries(index: number): void {
    const highlighted = this.legendTargets[index];

    if (!highlighted.classList.contains("Hidden"))
      this.allTargets.forEach(
        (target) => {
          if (target !== highlighted)
            target.classList.add("Faded");
        }
      );
  }

  highlightSegment(segment: string, series: DiagramSeries): void {
    if (this.highlighter !== null && !series.group.classList.contains("Hidden")) {
      this.allTargets.forEach((target) => target.classList.add("Faded"));
      this.highlighter.highlight(segment, series);
    }
  }

  clearHighlight(): void {
    this.allTargets.forEach((target) => target.classList.remove("Faded"));

    if (this.highlighter !== null)
      this.highlighter.clear();
  }

  toggleSeries(index: number): void {
    const hidden = this.legendTargets[index].classList.toggle("Hidden");
    this.legendItems[index].classList.toggle("Disabled", hidden);

    this.clearHighlight();
    this.highlightSeries(index);
  }

  attachSegments(): void {
    this.legendItems.forEach(
      (item, index) => {
        const series = this.seriesForTarget(this.legendTargets[index]);

        if (series !== null && this.highlighter !== null)
          item.querySelectorAll(".LegendSegment").forEach(
            (segmentItem) => {
              const segment = segmentItem.dataset.Segment;

              segmentItem.classList.add("Interactive");

              this.listen(segmentItem, "mouseenter", (event) => this.highlightSegment(segment, series));
              this.listen(segmentItem, "mouseleave", (event) => this.clearHighlight());
            }
          );
      }
    );
  }

  seriesForTarget(target: Element): DiagramSeries | null {
    return this.model.series.find((entry) => entry.group === target) || null;
  }

  destroy(): void {
    this.listeners.forEach((binding) => binding.target.removeEventListener(binding.type, binding.handler));
  }
}

class ElementHover {
  model: DiagramModel;
  overlay: DiagramOverlay;
  listeners: ListenerBinding[];
  hoveredLane: SVGElement | null;

  constructor(model: DiagramModel, overlay: DiagramOverlay) {
    this.model = model;
    this.overlay = overlay;
    this.listeners = [];
    this.hoveredLane = null;

    this.model.elements.forEach(
      (entry) => {
        this.listen(entry.element, "mouseenter", (event) => this.show(entry, event));
        this.listen(entry.element, "mousemove", (event) => this.overlay.tooltip.positionAtCursor(event));
        this.listen(entry.element, "mouseleave", (event) => this.hide());
      }
    );
  }

  listen(target: EventTarget, type: string, handler: EventListener): void {
    target.addEventListener(type, handler);
    this.listeners.push({ target: target, type: type, handler: handler });
  }

  show(entry: DiagramShape, event: MouseEvent): void {
    const tooltip = this.overlay.tooltip;
    tooltip.clear();

    if (entry.structured) {
      if (entry.header !== "")
        tooltip.addHeader(entry.header);

      tooltip.addRow(entry.color, entry.category, entry.range);
    }
    else
      tooltip.addLabelRow(entry.color, entry.label);

    tooltip.positionAtCursor(event);
    this.highlightLane(entry);
  }

  highlightLane(entry: DiagramShape): void {
    this.clearLane();

    if (entry.lane !== "") {
      const lanes = Array.from(this.model.svg.querySelectorAll(".Lane"));
      const lane = lanes.find((candidate) => candidate.getAttribute("data-lane") === entry.lane);

      if (lane instanceof SVGElement) {
        lane.classList.add("Highlighted");
        this.hoveredLane = lane;
      }
    }
  }

  clearLane(): void {
    if (this.hoveredLane !== null) {
      this.hoveredLane.classList.remove("Highlighted");
      this.hoveredLane = null;
    }
  }

  hide(): void {
    this.overlay.hide();
    this.clearLane();
  }

  destroy(): void {
    this.listeners.forEach((binding) => binding.target.removeEventListener(binding.type, binding.handler));
  }
}

export class DiagramInteraction {
  model: DiagramModel;
  overlay: DiagramOverlay | null;
  seriesHover: SeriesHover | null;
  segmentHighlighter: SegmentHighlighter | null;
  legendController: LegendController | null;
  elementHover: ElementHover | null;

  constructor(svg: SVGSVGElement, legend: Element | null, labels: any, mode: string, crosshairs: Set<string>, indexDimension: string, snap: number) {
    this.overlay = null;
    this.seriesHover = null;
    this.segmentHighlighter = null;
    this.legendController = null;
    this.elementHover = null;

    this.model = new DiagramModel(svg);

    if (this.model.series.length > 0 || this.model.elements.length > 0) {
      this.overlay = new DiagramOverlay(svg, svg.parentNode, this.model.series);

      if (this.model.series.length > 0)
        this.seriesHover = new SeriesHover(svg, this.model, this.overlay, mode, crosshairs, indexDimension, snap);

      if (this.model.elements.length > 0)
        this.elementHover = new ElementHover(this.model, this.overlay);

      this.segmentHighlighter = new SegmentHighlighter(this.overlay);
    }

    if (legend !== null)
      this.legendController = new LegendController(svg, this.model, legend, labels, this.segmentHighlighter);
  }

  destroy(): void {
    this.seriesHover?.destroy();
    this.elementHover?.destroy();
    this.legendController?.destroy();
    this.segmentHighlighter?.destroy();
    this.overlay?.remove();
  }
}

interactivityRegistration.register("Diagram", function (element) { return new Diagram(element); });
